import { LitElement, html, css, utils } from '@hh/global.js'

const { debounce } = utils

class HHHeaderSearchDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    isMobileReady: {
      type: Boolean,
      reflect: true,
      attribute: 'is-mobile-ready'
    },
    filled: {
      type: Boolean,
      reflect: true
    },
    filledResult: {
      type: Boolean,
      reflect: true,
      attribute: 'filled-result'
    },
    mod: {
      type: String,
      attribute: 'mod'
    },
    _scrollTop: {
      type: Number,
    }
  }

  constructor() {
    super()
    this.open = false
    this.isMobileReady = false
    this.filled = false
    this.mod = this.getAttribute('data-mod')

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event)
    }, 300)

    this.searchInput.addEventListener(
      'input',
      this.debouncedOnChange.bind(this)
    )

    this.searchClear.addEventListener('click', this.onClear.bind(this))

    if (this.mod == 'mobile') {
      this.searchInput.addEventListener('focusin', () => {
        if (this.searchInput.getAttribute('data-auto-focused') == 'true') {
          this.searchInput.removeAttribute('data-auto-focused')
          return false
        }
        this.isMobileReady = true
      })

      window.addEventListener('modalClosed', this.onClose.bind(this))
      this.searchInputClose.addEventListener('click', this.onClose.bind(this))
    }

    this.searchDrawerCloseEls.forEach(searchDrawerClose => searchDrawerClose.addEventListener('click', this.closeSearchDrawer.bind(this)))
  }

  connectedCallback() {
    super.connectedCallback()
    this.overlay.addEventListener('click', this.closeSearchDrawer.bind(this))
    window.addEventListener('triggerHeaderSearch', this.openSearchDrawer.bind(this))
    window.addEventListener('triggerAutoComplete', this.onAutoComplete.bind(this))
    this._handleKeyDown = this._handleKeyDown.bind(this)
    window.addEventListener('keydown', this._handleKeyDown)
  }

  _handleKeyDown(e) {
    if ((e.key === 'Escape' || e.keyCode === 27) && this.open) {
      this.closeSearchDrawer()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.overlay.removeEventListener('click', this.closeSearchDrawer)
    window.removeEventListener('triggerHeaderSearch', this.openSearchDrawer)
    window.removeEventListener('triggerAutoComplete', this.onAutoComplete)
    window.removeEventListener('keydown', this._handleKeyDown)
  }

  firstUpdated() {
  }

  onClose() {
    this.isMobileReady = false
    this.filledResult = false
    this.filled = false
    this.searchInput.value = ''
  }

  onChange() {
    const searchTerm = this.searchInput.value.trim()

    if (!searchTerm.length) {
      this.onClose()
      return;
    }

    this.filled = true
    this.isMobileReady = true
    this.getSearchResults(searchTerm)
  }

  getSearchResults(searchTerm) {
    fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status)
          // this.close()
          throw error
        }

        return response.text()
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML
        this.searchResults.innerHTML = resultsMarkup
        this.filledResult = true
      })
      .catch((error) => {
        throw error
      });
  }

  onAutoComplete(e) {
    if (e.detail?.key) {
      this.searchInput.value = e.detail?.key
      this.searchInput.dispatchEvent(new Event('change', { bubbles: true }))
      this.onChange()
    }
  }

  onClear(e) {
    this.searchInput.value = ''
    this.searchInput.dispatchEvent(new Event('change', { bubbles: true }))
    this.onChange()
  }

  // Open and close methods
  openSearchDrawer(e) {
    this.open = true
    document.querySelector('.header-search__trigger')?.setAttribute('aria-expanded', 'true')
    setTimeout(() => {
      this.querySelector('[slot="input"]')?.focus()
      this.querySelector('[slot="close-button"]')?.classList.add('focus-visible')
    }, 100)
  }

  closeSearchDrawer() {
    this.open = false
    this.currentMenu = ''
    document.querySelector('.header-search__trigger')?.setAttribute('aria-expanded', 'false')
  }

  updated(changedProperties) {
    if (changedProperties.has('open') && typeof changedProperties.get('open') !== 'undefined') {
      this.toggleOverlay()
      this.toggleBodyScroll()
    }
  }

  toggleOverlay() {
    this.overlay.classList[this.open ? 'add' : 'remove']('active')
  }

  toggleBodyScroll() {
    if (this.open) {
      this._scrollTop = window.scrollY
    }

    document.body.classList[this.open ? 'add' : 'remove']('no-scroll')

    if (!this.open) {
      window.scrollTo(0, this._scrollTop)
      setTimeout(() => {
        this.stickyHeader.externalRevealHeader()
      }, 0)
    } else {
      document.body.scrollTo(0, this._scrollTop)
    }
  }

  render() {
    return html`
      <div class="wrapper">
        <hh-focus-trap active="${this.open}">
          <slot name="close-button"></slot>
          <slot name="content"></slot>
        </hh-focus-trap>
      </div>
    `
  }

  static styles = css`
    :host {
      box-sizing: border-box;
    }

    *,
    *::before,
    *::after {
      box-sizing: inherit;
    }
  `

  // Getters
  get windowHeight() {
    return window.innerHeight
  }

  get totalHeaderHeight() {
    return this.header.offsetHeight + this.announcementBar.offsetHeight
  }

  get header() {
    return document.querySelector('.js-header') ?? null
  }

  get stickyHeader() {
    return document.querySelector('hh-sticky-header')
  }

  get overlay() {
    return document.querySelector('.js-header-search-overlay') ?? null
  }

  get searchInput() {
    return this.querySelector('.js-search-input input') ?? null
  }

  get searchResults() {
    return this.querySelector('.js-search-results') ?? null
  }

  get searchDrawerCloseEls() {
    return this.querySelectorAll('.js-search-drawer-close')
  }

  get searchInputClose() {
    return this.querySelector('.js-search-input-close') ?? null
  }

  get searchClear() {
    return this.querySelector('.js-search-clear') ?? null
  }
}

customElements.define('hh-header-search-drawer', HHHeaderSearchDrawer)
